import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { XCircle, CheckCircle, ArrowRight, Zap, Sparkles } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/lib/supabase';
import { useAccountStatus } from '@/hooks/useAccountStatus';

interface PlanDetails {
  name: string;
  interval: string;
}

interface FeedbackForm {
  feedback: string;
}

export default function PurchaseSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [planDetails] = useState<PlanDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [isCancelled] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const { toast } = useToast();
  const { register, handleSubmit, formState: { isSubmitting } } = useForm<FeedbackForm>();
  const { refresh: refreshAccountStatus } = useAccountStatus();
  
  // Check if user came from onboarding with skipBilling
  const skipBilling = searchParams.get('skipBilling') === 'true';

  const didNavigateRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    const markOnboardingComplete = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Mark onboarding as completed when user reaches purchase success
          const { error } = await supabase
            .schema('marketing_and_customer_success')
            .from('profiles')
            .update({ onboarding_completed: true })
            .eq('id', session.user.id);
          
          if (error) {
            console.error('[PurchaseSuccess] Error marking onboarding complete:', error);
          } else {
            console.log('[PurchaseSuccess] Successfully marked onboarding as complete');
          }
        }
      } catch (error) {
        console.error('[PurchaseSuccess] Exception marking onboarding complete:', error);
      }
    };

    const cleanCache = async () => {
      // Only clean cache if not in skipBilling mode
      if (skipBilling) {
        setLoading(false);
        return;
      }

      const session = await supabase.auth.getSession()
      if (!session.data.session) {
        throw new Error('No session found')
      }

      const workerUrl = import.meta.env.VITE_WORKERS_URL

      try {
        const response = await fetch(`${workerUrl}/billing-functions/clean_cache`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`
          },
          keepalive: true,
        })

        if (!response.ok) {
          console.error('Failed to clean billing cache');
        }
      } catch (error) {
        console.error('Error cleaning billing cache:', error);
      }
    };
    
    const syncBillingData = async (retryCount = 0): Promise<boolean> => {
      if (skipBilling) return true;
      
      const maxRetries = 5;
      const retryDelay = 2000; // 2 seconds
      
      try {
        const session = await supabase.auth.getSession()
        if (!session.data.session) {
          throw new Error('No session found')
        }

        const workerUrl = import.meta.env.VITE_WORKERS_URL
        console.log(`[PurchaseSuccess] Syncing billing data (attempt ${retryCount + 1}/${maxRetries + 1})`)

        const sessionId = searchParams.get('session_id');
        if (!sessionId) {
          console.log('[PurchaseSuccess] Warning: No session_id found in URL params, sync will use fallback methods');
        }
        // Always force refresh on purchase success
        const syncUrl = sessionId 
          ? `${workerUrl}/billing-functions/customer_sync?session_id=${sessionId}&force_refresh=true`
          : `${workerUrl}/billing-functions/customer_sync?force_refresh=true`;
          
        const response = await fetch(syncUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`
          },
          keepalive: true,
        })

        if (!response.ok) {
          console.error('[PurchaseSuccess] Failed to sync billing data:', response.status);
          throw new Error('Sync failed');
        }
        
        // Check if subscription was created
        const { data: subscription } = await supabase
          .schema('basejump')
          .from('billing_subscriptions')
          .select('*')
          .single();
          
        if (!subscription && retryCount < maxRetries) {
          console.log('[PurchaseSuccess] No subscription found yet, retrying...');
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return syncBillingData(retryCount + 1);
        }
        
        console.log('[PurchaseSuccess] Billing sync successful, subscription:', subscription?.id);
        
        // After successful billing sync, refresh the account status
        console.log('[PurchaseSuccess] Refreshing account status...');
        try {
          await refreshAccountStatus();
          console.log('[PurchaseSuccess] Account status refreshed');
        } catch (error) {
          console.error('[PurchaseSuccess] Error refreshing account status:', error);
        }
        
        return true;
      } catch (error) {
        console.error('[PurchaseSuccess] Error syncing billing data:', error);
        
        if (retryCount < maxRetries) {
          console.log(`[PurchaseSuccess] Retrying sync in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return syncBillingData(retryCount + 1);
        }
        
        return false;
      }
    };

    // Always mark onboarding as complete when reaching this page
    markOnboardingComplete();
    cleanCache();
    
    const fetchSessionDetails = async () => {
      // Skip fetching session details if in skipBilling mode
      if (skipBilling) {
        if (!didNavigateRef.current) {
          didNavigateRef.current = true;
          navigate('/dashboard', { replace: true });
        }
        return;
      }
      
      const sessionId = searchParams.get('session_id');
      console.log('[PurchaseSuccess] Session ID from URL:', sessionId);
      console.log('[PurchaseSuccess] Full URL:', window.location.href);
      
      // Always sync billing data, even without session_id
      // This handles cases where user navigates directly or session_id is missing
      await syncBillingData();
      if (!didNavigateRef.current) {
        didNavigateRef.current = true;
        navigate('/dashboard', { replace: true });
      }
      
      if (!sessionId) {
        console.log('[PurchaseSuccess] No session_id in URL, but sync attempted anyway');
      } else {
        console.log('[PurchaseSuccess] Session ID passed to sync:', sessionId);
      }
    };

    fetchSessionDetails();
    
    // Trigger brand convergence search for the user's brand
    const triggerBrandProcessing = async () => {
      // Generate a unique processing key based on session
      const sessionId = searchParams.get('session_id') || 'default';
      const processingKey = `brand_processing_${sessionId}`;
      
      // Check if brand processing already run in this session using localStorage
      if (localStorage.getItem(processingKey) === 'completed') {
        console.log('🔍 DEBUG: Brand processing already triggered, skipping duplicate call');
        return;
      }

      try {
        console.log('🔍 DEBUG: Initiating brand processing in PurchaseSuccess');
        // First get the session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.error('🔍 DEBUG: No active session found when trying to trigger brand processing');
          return;
        }
        
        console.log('🔍 DEBUG: Got active session, user ID:', session.user.id);
        
        // Find user's active brand
        const { data: activeBrands, error } = await supabase
          .from('brands')
          .select('id, brand_name, status')
          .eq('status', 'ACTIVE')
          .limit(1);
          
        if (error) {
          console.error('🔍 DEBUG: Error finding active brand:', error);
          return;
        }
        
        if (!activeBrands || activeBrands.length === 0) {
          console.log('🔍 DEBUG: No active brands found to process. Checking for any brands...');
          
          // Try to find any brand, even if not active
          const { data: anyBrands, error: anyBrandsError } = await supabase
            .from('brands')
            .select('id, brand_name, status')
            .limit(5);
            
          if (anyBrandsError) {
            console.error('🔍 DEBUG: Error finding any brands:', anyBrandsError);
            return;
          }
          
          if (anyBrands && anyBrands.length > 0) {
            console.log('🔍 DEBUG: Found brands but none are active:', 
              anyBrands.map(b => ({ id: b.id, name: b.brand_name, status: b.status })));
            return;
          }
          
          console.log('🔍 DEBUG: No brands found at all for this user');
          return;
        }
        
        const brandId = activeBrands[0].id;
        console.log('🔍 DEBUG: Found active brand to process:', activeBrands[0].brand_name, 'ID:', brandId);
        
        // Mark brand processing as started in localStorage before making the request
        localStorage.setItem(processingKey, 'started');

        // Call the process_updated_brand endpoint to trigger convergence search
        const workerUrl = import.meta.env.VITE_WORKERS_URL;
        console.log('🔍 DEBUG: Worker URL:', workerUrl);
        
        const response = await fetch(`${workerUrl}/rainfactory/brand-genius/process_updated_brand`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          keepalive: true,
          body: JSON.stringify({
            brandId,
            userId: session.user.id,
            source: 'purchase_success_page'
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('🔍 DEBUG: Failed to trigger brand processing:', response.status, errorText);
          return;
        }
        
        const result = await response.json();
        console.log('🔍 DEBUG: Brand processing initiated successfully:', result);
        
        // Mark as completed after successful processing
        localStorage.setItem(processingKey, 'completed');
      } catch (error) {
        console.error('🔍 DEBUG: Error triggering brand processing:', error);
      } finally {
        // no-op; navigation handled elsewhere
      }
    };
    
    // Always trigger brand processing, regardless of skipBilling mode
    triggerBrandProcessing();
  }, [searchParams, skipBilling]);

  const onSubmitFeedback = async (data: FeedbackForm) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .schema('marketing_and_customer_success')
        .from('user_events')
        .insert({
          user_id: user.id,
          event_type: 'CANCELLATION_FEEDBACK',
          event_data: { feedback: data.feedback },
          function_name: 'web_cancellation_feedback'
        });

      if (error) throw error;

      setFeedbackSubmitted(true);
      toast({
        title: "Thank you for your feedback",
        description: "We appreciate you taking the time to help us improve.",
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Error submitting feedback",
        description: "Please try again later.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative container mx-auto flex items-center justify-center min-h-screen p-4">
          <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg p-8">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
            <div className="relative text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-2xl shadow-sm">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
              </div>
              <h2 className="text-xl font-semibold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                Setting up your success... 
              </h2>
              <p className="text-gray-600">Please wait while we finalize your account</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="relative container mx-auto flex items-center justify-center min-h-screen p-6 max-w-6xl">
        <div className="w-full max-w-4xl">
          {/* Enhanced Header Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-lg mb-8 p-8 text-center">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-100/30 to-transparent rounded-full translate-y-16 -translate-x-16" />
            <div className="relative">
              <div className="flex justify-center mb-6">
                {isCancelled ? (
                  <div className="p-4 bg-red-50 rounded-2xl shadow-sm">
                    <XCircle className="h-16 w-16 text-red-500" />
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-200/30 to-blue-200/30 rounded-2xl blur-xl scale-110"></div>
                    <div className="relative p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                      <CheckCircle className="h-16 w-16 text-green-600" />
                    </div>
                    <div className="absolute -top-2 -right-2 p-2 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl shadow-sm animate-pulse">
                      <Sparkles className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                )}
              </div>
              <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-4">
                {isCancelled ? 'Subscription Cancelled' : skipBilling ? 'Setup Complete!' : 'Welcome Aboard!'}
              </h1>
              {isCancelled ? (
                <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                  We're sorry to see you go. Your subscription has been cancelled and will remain active until the end of your current billing period.
                </p>
              ) : skipBilling ? (
                <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                  Your brand foundation is ready. The next step is to build your brand campaign so Rainfactory can start creating content that matches your unique voice.
                </p>
              ) : (
                <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                  Your subscription is now active! The next step is to build your brand campaign so Rainfactory can start creating content that matches your unique voice.
                </p>
              )}
            </div>
          </div>
          
          {/* Main Content Section */}
          <div>
          {isCancelled ? (
            !feedbackSubmitted ? (
              <div className="relative overflow-hidden bg-gradient-to-br from-red-50/80 via-orange-50/60 to-white rounded-2xl border border-red-200/60 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-red-50/30 via-transparent to-orange-50/20" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
                <div className="relative p-8">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-red-800 to-orange-700 bg-clip-text text-transparent mb-2">
                      Help Us Improve
                    </h2>
                    <p className="text-gray-600">Your feedback is valuable to us</p>
                  </div>
                  <form onSubmit={handleSubmit(onSubmitFeedback)} className="space-y-6">
                    <div>
                      <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-3">
                        Would you mind sharing why you decided to cancel?
                      </label>
                      <div className="relative">
                        <Textarea
                          id="feedback"
                          placeholder="Your feedback helps us improve our service..."
                          {...register('feedback')}
                          className="min-h-[120px] bg-white/80 backdrop-blur-sm border-white/50 shadow-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50"
                        />
                      </div>
                    </div>
                    <div className="group relative overflow-hidden bg-gradient-to-br from-red-500/90 to-orange-600/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5" />
                      <Button 
                        type="submit" 
                        disabled={isSubmitting} 
                        className="relative h-auto p-4 w-full bg-transparent hover:bg-transparent border-0 text-white shadow-none font-semibold"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            <span>Submitting...</span>
                          </div>
                        ) : (
                          'Submit Feedback'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="relative overflow-hidden bg-gradient-to-br from-green-50/80 via-emerald-50/60 to-white rounded-2xl border border-green-200/60 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 via-transparent to-emerald-50/20" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
                <div className="relative p-8 text-center">
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-200 rounded-2xl shadow-sm mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-green-800 to-emerald-700 bg-clip-text text-transparent mb-2">
                      Thank You!
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Your feedback has been received and will help us improve our service.
                    </p>
                  </div>
                  <div className="group relative overflow-hidden bg-gradient-to-br from-gray-500/90 to-slate-600/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 max-w-sm mx-auto">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5" />
                    <Button 
                      onClick={() => navigate('/')} 
                      className="relative h-auto p-4 w-full bg-transparent hover:bg-transparent border-0 text-white shadow-none font-semibold"
                    >
                      Return to Homepage
                    </Button>
                  </div>
                </div>
              </div>
            )
          ) : skipBilling ? (
            /* Enhanced Single Action Section for Skip Billing */
            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50/80 via-green-50/60 to-white rounded-2xl border border-emerald-200/60 shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-transparent to-green-50/20" />
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
              <div className="relative p-8 text-center">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-3 mb-4">
                    <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                      <Zap className="h-8 w-8 text-emerald-600" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-800 to-green-700 bg-clip-text text-transparent mb-4">
                    Ready to Build Your Brand Campaign
                  </h2>
                  <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                    Create your brand foundation that Rainfactory uses to generate content that perfectly matches your voice and style.
                  </p>
                </div>
                
                {/* Single Enhanced CTA */}
                <div className="group relative overflow-hidden bg-gradient-to-br from-emerald-500/90 to-green-600/90 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 max-w-lg mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5" />
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 via-transparent to-green-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    className="relative h-auto p-8 w-full bg-transparent hover:bg-transparent border-0 text-white shadow-none text-lg font-semibold"
                  >
                    <div className="flex items-center justify-center gap-4">
                      <span>Build Your Brand Campaign</span>
                      <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {planDetails ? (
                <p className="text-gray-600 mb-6">
                  You are now the proud owner of the {planDetails.name} plan{planDetails.interval === 'month' ? ' (monthly)' : planDetails.interval === 'year' ? ' (yearly)' : ''}. Your subscription has been successfully activated, and you now have access to all the features included in your plan.
                </p>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    Your subscription has been successfully activated. You now have access to all the features included in your plan.
                  </p>
                  {/* Enhanced Single Action Section for Paid Plans */}
                  <div className="relative overflow-hidden bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-white rounded-2xl border border-blue-200/60 shadow-lg">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
                    <div className="relative p-8 text-center">
                      <div className="mb-6">
                        <div className="inline-flex items-center gap-3 mb-4">
                          <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                            <Zap className="h-8 w-8 text-blue-600" />
                          </div>
                        </div>
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent mb-4">
                          Ready to Build Your Brand Campaign
                        </h2>
                        <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                          Create your brand foundation that Rainfactory uses to generate content that perfectly matches your voice and style.
                        </p>
                      </div>
                      
                      {/* Single Enhanced CTA */}
                      <div className="group relative overflow-hidden bg-gradient-to-br from-blue-500/90 to-indigo-600/90 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 max-w-lg mx-auto">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5" />
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-transparent to-indigo-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <Button 
                          onClick={() => navigate('/dashboard')}
                          className="relative h-auto p-8 w-full bg-transparent hover:bg-transparent border-0 text-white shadow-none text-lg font-semibold"
                        >
                          <div className="flex items-center justify-center gap-4">
                            <span>Build Your Brand Campaign</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                          </div>
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
} 